import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Car, Home as HomeIcon, Clock, ArrowRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterServices = (category) => {
    if (category === 'all') return services;
    return services.filter(s => s.category === category);
  };

  const getCategoryIcon = (category) => {
    return category === 'automobile' ? Car : HomeIcon;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20" data-testid="services-page">
      {/* Header */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-white mb-6">
              Nos services de nettoyage
            </h1>
            <p className="text-slate-400 text-lg md:text-xl leading-relaxed">
              Découvrez l'ensemble de nos prestations de nettoyage automobile à domicile et de nettoyage d'intérieur. Tous nos services sont réalisés avec des produits professionnels.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="all" className="w-full" onValueChange={setActiveTab}>
            <TabsList className="mb-12 bg-slate-100 p-1 rounded-full inline-flex">
              <TabsTrigger 
                value="all" 
                className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-all"
              >
                Tous les services
              </TabsTrigger>
              <TabsTrigger 
                value="automobile" 
                className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-auto"
              >
                <Car className="w-4 h-4 mr-2" />
                Automobile
              </TabsTrigger>
              <TabsTrigger 
                value="interieur" 
                className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-interieur"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Intérieur
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filterServices(activeTab).map((service, index) => {
                  const Icon = getCategoryIcon(service.category);
                  return (
                    <Card 
                      key={service.id} 
                      className="service-card overflow-hidden border-slate-100 hover:border-sky-200"
                      style={{ animationDelay: `${index * 0.1}s` }}
                      data-testid={`service-${service.id}`}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={service.image_url}
                          alt={service.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                            service.category === 'automobile' 
                              ? 'bg-slate-900 text-white' 
                              : 'bg-sky-500 text-white'
                          }`}>
                            <Icon className="w-3 h-3" />
                            {service.category === 'automobile' ? 'Auto' : 'Intérieur'}
                          </span>
                        </div>
                      </div>
                      
                      <CardContent className="p-6">
                        <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-2">
                          {service.name}
                        </h3>
                        <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                          {service.description}
                        </p>
                        
                        <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                          {service.duration && (
                            <div className="flex items-center gap-2 text-slate-500 text-sm">
                              <Clock className="w-4 h-4" />
                              {service.duration}
                            </div>
                          )}
                          <span className={`font-semibold text-sky-600 ${!service.duration ? 'ml-auto' : ''}`}>
                            {service.price_info}
                          </span>
                        </div>

                        <Link to={`/booking?service=${service.id}`}>
                          <Button className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid={`book-${service.id}`}>
                            Réserver ce service
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Info Section */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-['Outfit'] text-3xl font-bold text-slate-900 mb-6">
                Un service personnalisé à domicile
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Chez BA NETTOYAGE, nous nous déplaçons directement chez vous pour réaliser nos prestations. Plus besoin de vous déplacer ou de perdre du temps en concession.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                    <span className="text-sky-600 text-sm">✓</span>
                  </div>
                  Intervention dans toute l'Auvergne
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                    <span className="text-sky-600 text-sm">✓</span>
                  </div>
                  Produits et matériel professionnels fournis
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                    <span className="text-sky-600 text-sm">✓</span>
                  </div>
                  Tarifs transparents, sans surprise
                </li>
              </ul>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-8">
                  Demander un devis personnalisé
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1694678505383-676d78ea3b96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjYXIlMjBjbGVhbmluZyUyMGRldGFpbGluZyUyMHdhc2hpbmd8ZW58MHx8fHwxNzcyNjEzMjczfDA&ixlib=rb-4.1.0&q=85"
                alt="Nettoyage professionnel"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-6 shadow-lg border border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <span className="text-2xl">⭐</span>
                  </div>
                  <div>
                    <div className="font-['Outfit'] font-bold text-slate-900">100% Satisfaits</div>
                    <div className="text-slate-600 text-sm">Nos clients nous recommandent</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
